import React from 'react'
import Layout from '../components/layout'
import Meta from '../components/meta'
import tw, { styled } from 'twin.macro'
import {
	PromotionHero,
	PromotionCard,
	PromotionGrid,
	TopBanner,
} from '../components/promotions'

const EmptyStateMessage = styled.div`
	${tw`text-center py-12 px-4`}
	grid-column: 1 / -1;
`

const EmptyStateText = styled.p`
	${tw`text-gray-600 text-lg`}
`

/**
 * PromotionsPage - Main promotions landing page
 * This page showcases current and previous promotions with a hero section and separate grids
 * for active and expired promotions
 *
 * To add your images:
 * 1. Add images to /static/ folder or import from Contentful
 * 2. Replace imageSrc prop values with your image paths
 * 3. Update imageAlt with descriptive alt text
 *
 * To manage promotions:
 * - Add current promotions to the currentPromotions array
 * - Move expired promotions to the previousPromotions array
 */
const PromotionsPage = () => {
	// Current active promotions
	const currentPromotions = [
		{
			title: 'Dolphin Eon 120d + Skimmi 300 Bundle',
			description: `Maytronics Dolphin EON 120d + Skimmi 300 Bundle: Complete pool cleaning from floor to surface with intelligent cordless cleaning and automated solar-powered skimming. Clean your pool with two smart robotic cleaners working together.`,
			imageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/2RFhj6DjL12YPL0RkvUUbH/f1166cfb651f960ff555f193b1766c72/EON120d_-2B_SKIMMI300.jpg',
			imageAlt: 'Dolphin Eon 120d + Skimmi 300 Bundle',
			buttonImageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/2dmTO1lu7yv6x85A1KImQC/77d9b6b88c16c2631899f907e4e6c626/FindOutMore_YellowCTA.png',
			buttonImageHoverSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/6e8XjzEQNJrkiyjZLccKt4/2f72691ae655c5855e0bb7feed5d6803/FindOutMore_Yellow_WhenMouseHoversOverCTA.png',
			buttonImageAlt: 'FIND OUT MORE',
			buttonLink: 'https://mydolphin.com.au/product/dolphin-eon-120d-skimmi-300-bundle/',
			buttonVariant: 'primary',
			badges: [],
		},
		{
			title: 'Dolphin Liberty 600 + Skimmi 300 Bundle',
			description: `Take pool cleaning to the next level with the Dolphin Liberty 600 + Dolphin Skimmi 300 Bundle. Designed to work together for complete pool coverage, the Dolphin Liberty 600 pool cleaner tackles the floor, walls, waterline, steps and tanning ledges. At the same time, the solar-powered Dolphin Skimmi 300 robot skimmer continuously collects leaves, bugs, pollen and debris from the pool surface. Two intelligent robots. Complete cleaning from floor to surface.`,
			imageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/4cWnXXCOUUKN43tBl887WX/0f4359ec7be6d6ec4f9e764d5d942cde/LIB600_-2B_SKIMMI300.jpg',
			imageAlt: 'Dolphin Liberty 600 + Skimmi 300 Bundle',
			buttonImageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/2dmTO1lu7yv6x85A1KImQC/77d9b6b88c16c2631899f907e4e6c626/FindOutMore_YellowCTA.png',
			buttonImageHoverSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/6e8XjzEQNJrkiyjZLccKt4/2f72691ae655c5855e0bb7feed5d6803/FindOutMore_Yellow_WhenMouseHoversOverCTA.png',
			buttonImageAlt: 'FIND OUT MORE',
			buttonLink: 'https://mydolphin.com.au/product/dolphin-liberty-600-skimmi-300-bundle/',
			buttonVariant: 'primary',
			badges: [],
		},
		{
			title: 'Dolphin Liberty 200 & Skimmi 200 Bundle',
			description: `Keep your pool clean from the floor to the surface with the Dolphin Liberty 200 + Dolphin Skimmi 200 Bundle. Get powerful, convenient cordless floor, wall and waterline cleaning with the cordless Dolphin Liberty 200 pool cleaner, plus a free Skimmi 200 solar-powered surface skimmer. The Dolphin Liberty 200 features simple click-in-place charging, powerful active brushing and superior fine filtration, while Eco Mode keeps your pool clean throughout the week with short, efficient cleaning cycles. Above the water, the solar-powered Dolphin Skimmi 200 pool skimmer automatically navigates your pool using smart sensors, collecting leaves, bugs, pollen and other debris from the surface.`,
			imageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/6qapTiTKpRlMViC11xogd/f4713461519de1c7714ffa0d5b89a2dc/Skimmi200_Liberty200Bundle_D1.jpg',
			imageAlt: 'Dolphin Liberty 200 & Skimmi 200 Bundle',
			buttonImageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/2dmTO1lu7yv6x85A1KImQC/77d9b6b88c16c2631899f907e4e6c626/FindOutMore_YellowCTA.png',
			buttonImageHoverSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/6e8XjzEQNJrkiyjZLccKt4/2f72691ae655c5855e0bb7feed5d6803/FindOutMore_Yellow_WhenMouseHoversOverCTA.png',
			buttonImageAlt: 'FIND OUT MORE',
			buttonLink: 'https://mydolphin.com.au/product/dolphin-liberty-200-skimmi-200-bundle/',
			buttonVariant: 'primary',
			badges: [],
		},
	]

	// Previous/expired promotions
	const previousPromotions = [
		{
			title: '🏉 Footy Finals Flash Sale!',
			description: `The Footy Finals are here - and so are the savings!
						For a limited time only, save up to $700 across selected Dolphin robotic pool cleaners and Skimmi models.

						From Friday 18 September 2026 till Wednesday 30 September 2026 included.

						T&C’s Aplly`,
			imageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/TYilfBMwo3QPNruaNHKxb/f9e3ff6db69e817e845a5c3a311ad099/EcommFootyFinalsAsset_1500x1500.jpg',
			imageAlt: 'Footy Finals Flash Sale',
			//buttonImageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/2dmTO1lu7yv6x85A1KImQC/77d9b6b88c16c2631899f907e4e6c626/FindOutMore_YellowCTA.png',
			// buttonImageHoverSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/6e8XjzEQNJrkiyjZLccKt4/2f72691ae655c5855e0bb7feed5d6803/FindOutMore_Yellow_WhenMouseHoversOverCTA.png',
			//buttonImageAlt: 'FIND OUT MORE',
			// buttonLink: 'https://mydolphin.com.au/footy/',
			badges: [],
		},
		{
			title: 'Niya Sonar F3 at $999',
			description: `Cordless Niya Sonar F3 for $999 only!🚨
				Grab this exclusive online sale for $999 (RRP $1,199) - SAVE $200! 💦
				✨ Cordless convenience
				✨ Cleans floor, walls & waterline
				✨ Front & rear active brushes for deep cleaning
				✨ Self-parks at the pool edge
				✨ Easy-to-replace battery
				Hurry, while stocks last!
				T&Cs apply`,
			imageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/5eCNc4veF6bC8Yrov5sMOw/4dd78afbcc8ebc15d847c13f6d5f5a95/Niya-sonar-f3-promotions.png',
			imageAlt: 'Niya sale',
			badges: [],
		},
		{
			title: 'Prizes Every Week! T&Cs Apply',
			description: `📅 Campaign starts from Monday 3rd of
				November 2025 to Sunday 28th of December 2025 by midnight.

				🏖️ Campaign Details:
				Consumer will purchase a qualifying Maytronics
				product (Dolphin Robotic Pool Cleaner, Mineral
				Swim System) from a participating Elite Dealer
				Retailer (full list of dealers on mydolphin.com.au)
				and enter the campaign via mydolphin.com.au/summer.

				✅ Consumer to ensure all details are submitted `,
			imageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/2bpJucpjdsevPMuCHaPg50/4c6cd8835bcd8293a29b11d1b5a7d1fc/SummerGiveaway_Thumbnail.png',
			imageAlt: 'Summer giveaway prizes',

			badges: [],
		},
		{
			title: 'Huge Discount on the Niya Sonar F3!',
			description: 'Save $300 on the Niya Sonar F3.',
			imageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/5w8c8N41T8MdAdO9QCo79J/bbf651874c7ad121df8f182e1c63f476/BoxingDay_Thumbnail.png',
			imageAlt: 'Boxing day sale',
			badges: [],
		},
		{
			title: 'Share your Aussie Moments',
			description: `🎉 Get a chance to WIN a Dolphin S100, valued at $1,599, this Australia Day! 🇦🇺
				📸 Simply comment with a photo or video from your celebrations.
				Whether it’s a snag on the BBQ, fairy bread with the kids, or a watermelon helmet by the pool, share it with us! ☀️
				✨ Bonus: Share this post for extra chances to win!
				Campaign runs from 19 January to 1 February.
				T&Cs apply.`,
			imageSrc: 'https://images.ctfassets.net/ftcnv5up9dxv/MjAM87g5LCxB86eqD2ZT2/5c6980c36205695adda939cc44eed7bd/2025_AussieDayMomentsCampaign_1080x1080.jpg',
			imageAlt: 'Aussie Moments Campaign 2026',
			badges: [],
		},

	]

	// Top banner items
	const topBannerItems = []

	return (
		<Layout>
			<Meta
				title="Promotions | Maytronics"
				description="Discover our current and previous promotions and special offers on pool cleaners and accessories"
			/>

			{/* Top promotional banner */}
			{topBannerItems.length > 0 && <TopBanner items={topBannerItems} />}

			{/* Hero section */}
			<PromotionHero
				imageSrc="https://images.ctfassets.net/ftcnv5up9dxv/CMdFoHJHHlrRjhoTkggRv/40a0a845ff10a8eeaf80a167c3504938/HeaderBanner_PromotionsPage.png"
				imageAlt="Promotional offer"
			/>

			{/* Current Promotions grid */}
			<PromotionGrid
				titleImage="https://images.ctfassets.net/ftcnv5up9dxv/3s9MJ0eJVFWyUPn1WBBRI6/9f169acd496a389a2eabdea5c8599947/CurrentPromotions.png"
				titleImageAlt="Current Promotions"
			>
				{currentPromotions.length > 0 ? (
					currentPromotions.map((promotion, index) => (
						<PromotionCard
							key={index}
							title={promotion.title}
							description={promotion.description}
							imageSrc={promotion.imageSrc}
							imageAlt={promotion.imageAlt}
							buttonImageSrc={promotion.buttonImageSrc}
							buttonImageHoverSrc={promotion.buttonImageHoverSrc}
							buttonImageAlt={promotion.buttonImageAlt}
							buttonLink={promotion.buttonLink}
							buttonVariant={promotion.buttonVariant}
							badges={promotion.badges}
						/>
					))
				) : (
					<EmptyStateMessage>
						<EmptyStateText>No current promotions</EmptyStateText>
					</EmptyStateMessage>
				)}
			</PromotionGrid>

			{/* Previous Promotions grid */}
			{previousPromotions.length > 0 && (
				<PromotionGrid
					title="Previous Promotions"
					titleImage="https://images.ctfassets.net/ftcnv5up9dxv/6fzRNT3CPgEVTBItAvsiEH/9a4fa8e184921a383745929d47befd5b/PreviousPromotions-removebg-preview.png"

				>
					{previousPromotions.map((promotion, index) => (
						<PromotionCard
							key={index}
							title={promotion.title}
							description={promotion.description}
							imageSrc={promotion.imageSrc}
							imageAlt={promotion.imageAlt}
							buttonImageSrc={promotion.buttonImageSrc}
							buttonImageHoverSrc={promotion.buttonImageHoverSrc}
							buttonImageAlt={promotion.buttonImageAlt}
							buttonLink={promotion.buttonLink}
							buttonVariant={promotion.buttonVariant}
							badges={promotion.badges}
						/>
					))}
				</PromotionGrid>
			)}
		</Layout>
	)
}

export default PromotionsPage
